import React, { useEffect, useRef } from 'react';
import { Cpu, Check, Eye, Info, Play, Clock, Crown, Volume2 } from 'lucide-react';

interface RenderVersion {
  version: number;
  videoUrl: string;
  vttUrl: string;
  audioMode: string;
  voiceId: string;
  createdAt: string;
}

interface PreviewTabProps {
  processedVideoUrl: string;
  legendaUrl: string;
  voiceName: string;
  // Studio render versions
  renderedVersions: RenderVersion[];
  activeRenderIndex: number;
  setActiveRenderIndex: (idx: number) => void;
}

export function PreviewTab({
  processedVideoUrl, legendaUrl, voiceName,
  renderedVersions, activeRenderIndex, setActiveRenderIndex,
}: PreviewTabProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  // Active version data
  const activeVersion = activeRenderIndex >= 0 && activeRenderIndex < renderedVersions.length
    ? renderedVersions[activeRenderIndex]
    : null;

  const displayVideoUrl = activeVersion?.videoUrl || processedVideoUrl;
  const displayVttUrl = activeVersion?.vttUrl || legendaUrl;
  const displayVoice = activeVersion?.voiceId || voiceName;

  // Auto-play when new version arrives
  useEffect(() => {
    if (activeVersion && videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [activeRenderIndex, activeVersion?.videoUrl]);

  const fmtDate = (iso: string) => {
    try {
      const d = new Date(iso);
      return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
    } catch { return iso; }
  };

  const hasContent = !!displayVideoUrl;

  return (
    <div className="recorder-modal-grid">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.78rem', fontWeight: 600, color: '#a855f7' }}>
          <Eye size={14} /> Visualização do Vídeo Final de Estúdio
        </label>
        {hasContent ? (
          <div style={{ background: '#000', borderRadius: 8, overflow: 'hidden', display: 'flex', justifyContent: 'center', width: '100%', aspectRatio: '16 / 9', maxHeight: '380px', border: '1px solid #1e293b' }}>
            <video
              ref={videoRef}
              key={displayVideoUrl}
              src={displayVideoUrl}
              controls
              crossOrigin="anonymous"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            >
              {displayVttUrl && (
                <track
                  src={displayVttUrl}
                  kind="subtitles"
                  srcLang="pt"
                  label="Português"
                  default
                />
              )}
            </video>
          </div>
        ) : (
          <div style={{
            flex: 1,
            aspectRatio: '16 / 9',
            maxHeight: '380px',
            background: '#020617',
            borderRadius: 8,
            border: '1.5px dashed #334155',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            color: '#64748b',
            padding: 24,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2.5rem' }}><Cpu size={40} style={{ color: '#475569' }} /></div>
            <div style={{ fontSize: '.8rem', fontWeight: 700, color: '#94a3b8' }}>
              Vídeo final de estúdio pendente
            </div>
            <p style={{ fontSize: '.72rem', maxWidth: 360, margin: 0 }}>
              Grave um vídeo, transcreva e use o botão <strong>Renderizar</strong> na aba <strong>Estúdio</strong> para gerar o vídeo final.
            </p>
          </div>
        )}
        {hasContent && (
          <div style={{ fontSize: '.72rem', color: '#22c55e', display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
            <Check size={14} /> Vídeo de estúdio renderizado
            {displayVoice && <> com a voz <strong>{displayVoice}</strong></>}
            {activeVersion && <> · {activeVersion.audioMode === 'bruto' ? 'Original' : 'Premium'}</>}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, height: '100%', overflowY: 'auto', paddingRight: 4 }}>
        {/* ── Versões Renderizadas ── */}
        {renderedVersions.length > 0 && (
          <div style={{ background: '#1e293b', padding: 16, borderRadius: 8, border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h4 style={{ fontSize: '.85rem', fontWeight: 800, margin: 0, color: '#a855f7', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Play size={16} /> Versões Renderizadas ({renderedVersions.length})
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[...renderedVersions].reverse().map((rv, revIdx) => {
                const originalIdx = renderedVersions.length - 1 - revIdx;
                const isActive = originalIdx === activeRenderIndex;
                return (
                  <div
                    key={rv.version}
                    onClick={() => setActiveRenderIndex(originalIdx)}
                    style={{
                      padding: '10px 12px', borderRadius: 8,
                      border: `1.5px solid ${isActive ? '#a855f7' : '#334155'}`,
                      background: isActive ? 'rgba(168, 85, 247, 0.08)' : '#0f172a',
                      cursor: 'pointer', transition: 'all 0.2s',
                      display: 'flex', alignItems: 'center', gap: 10,
                    }}
                  >
                    {/* Version badge */}
                    <span style={{
                      fontSize: '.65rem', fontWeight: 800,
                      background: isActive ? '#a855f7' : '#334155',
                      color: isActive ? '#fff' : '#94a3b8',
                      padding: '2px 8px', borderRadius: 4,
                      minWidth: 28, textAlign: 'center',
                    }}>
                      v{rv.version}
                    </span>

                    {/* Mode icon */}
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {rv.audioMode === 'premium' ? (
                        <Crown size={12} style={{ color: '#22c55e' }} />
                      ) : (
                        <Volume2 size={12} style={{ color: '#64748b' }} />
                      )}
                      <span style={{ fontSize: '.68rem', fontWeight: 600, color: rv.audioMode === 'premium' ? '#22c55e' : '#94a3b8' }}>
                        {rv.audioMode === 'premium' ? 'Premium' : 'Original'}
                      </span>
                    </span>

                    {/* Voice */}
                    {rv.voiceId && (
                      <span style={{ fontSize: '.62rem', color: '#64748b', padding: '1px 6px', background: '#1e293b', borderRadius: 3 }}>
                        {rv.voiceId}
                      </span>
                    )}

                    {/* Date */}
                    <span style={{ marginLeft: 'auto', fontSize: '.58rem', color: '#475569', display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Clock size={10} /> {fmtDate(rv.createdAt)}
                    </span>

                    {/* Active badge */}
                    {isActive && (
                      <span style={{
                        fontSize: '.55rem', fontWeight: 700,
                        background: '#22c55e', color: '#000',
                        padding: '1px 6px', borderRadius: 3,
                      }}>
                        ATIVO
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Detalhes da Mídia ── */}
        <div style={{ background: '#1e293b', padding: 20, borderRadius: 8, border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <h4 style={{ fontSize: '.85rem', fontWeight: 800, margin: 0, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Info size={16} /> Detalhes da Mídia Processada
          </h4>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: '.72rem', color: '#94a3b8' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #334155', paddingBottom: 6 }}>
              <span>Status do Build:</span>
              <span style={{ color: hasContent ? '#22c55e' : '#f59e0b', fontWeight: 700 }}>
                {hasContent ? 'CONCLUÍDO' : 'PENDENTE'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #334155', paddingBottom: 6 }}>
              <span>Modo de Áudio:</span>
              <span style={{ color: '#f8fafc', fontWeight: 700 }}>
                {activeVersion?.audioMode === 'premium' ? '👑 Premium' : '🔊 Original'}
              </span>
            </div>
            {displayVoice && (
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #334155', paddingBottom: 6 }}>
                <span>Voz Utilizada:</span>
                <span style={{ color: '#f8fafc', fontWeight: 700 }}>{displayVoice}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #334155', paddingBottom: 6 }}>
              <span>Legenda VTT:</span>
              <span style={{ color: displayVttUrl ? '#22c55e' : '#f8fafc', fontWeight: 700 }}>
                {displayVttUrl ? 'Ativa (Sincronizada)' : 'Inativa'}
              </span>
            </div>
            {activeVersion && (
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #334155', paddingBottom: 6 }}>
                <span>Versão:</span>
                <span style={{ color: '#a855f7', fontWeight: 700 }}>v{activeVersion.version}</span>
              </div>
            )}
          </div>

          {hasContent ? (
            <div style={{ fontSize: '.72rem', color: '#94a3b8', lineHeight: 1.4, background: '#0f172a', padding: 12, borderRadius: 6, border: '1px solid #334155', marginTop: 6 }}>
              <p style={{ margin: '0 0 6px 0', fontWeight: 700, color: '#38bdf8' }}>Dica de Publicação</p>
              Assista ao vídeo gerado para validar a entonação da voz da IA e as legendas. Se tudo estiver correto, utilize o painel de aprovação abaixo para salvar a versão final.
            </div>
          ) : (
            <div style={{ fontSize: '.72rem', color: '#ef4444', background: 'rgba(239, 68, 68, 0.1)', padding: 10, borderRadius: 6, border: '1px solid rgba(239, 68, 68, 0.2)' }}>
              Nenhuma versão renderizada foi encontrada. Use o botão <strong>Renderizar</strong> no Estúdio para gerar.
            </div>
          )}
        </div>

        {hasContent && (
          <div style={{ background: '#1e293b', padding: 20, borderRadius: 8, border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h4 style={{ fontSize: '.85rem', fontWeight: 800, margin: 0, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Check size={16} /> Aprovação do Resultado Final
            </h4>
            <p style={{ fontSize: '.72rem', color: '#94a3b8', margin: 0, lineHeight: 1.4 }}>
              Se o áudio e a legenda ficaram corretos, você pode salvar este vídeo como avulso ou vinculá-lo ao catálogo utilizando os botões de ação localizados no rodapé deste modal.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
